import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../../services/api'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  ChevronRight,
  FileCheck,
  Clock,
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  Loader2,
  Send,
  User,
  BookOpen,
  Folder,
  Download,
  File as FileIcon,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function submissionStatusConfig(status) {
  switch (status) {
    case 'reviewed':
      return { label: 'Reviewed', icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' }
    case 'needs_changes':
      return { label: 'Needs Changes', icon: AlertTriangle, color: 'text-orange-600 dark:text-orange-400' }
    case 'submitted':
      return { label: 'Submitted', icon: Clock, color: 'text-blue-600 dark:text-blue-400' }
    default:
      return { label: 'Unknown', icon: Clock, color: 'text-gray-400' }
  }
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })
}

// ─── Submission List ─────────────────────────────────────────────────────────

function SubmissionList() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['trainer-digital-lab-submissions', statusFilter],
    queryFn: async () => {
      const params = statusFilter ? `?status=${statusFilter}` : ''
      const { data } = await api.get(`/digital-lab/trainer/submissions${params}`)
      return data
    },
  })

  const submissions = data?.submissions || []

  if (isLoading) return <LoadingSpinner />

  if (error) {
    return (
      <div className="empty-state py-16">
        <AlertTriangle className="w-10 h-10 text-red-400 dark:text-red-500 mx-auto mb-3" />
        <p className="text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {error.message || 'Failed to load submissions'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header">
        <div>
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium mb-3 block">
            Trainer · Digital Lab
          </span>
          <h1 className="text-2xl sm:text-3xl text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}>
            Project Submissions
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Student project submissions awaiting your review.
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mr-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Filter:
        </span>
        {['', 'submitted', 'reviewed', 'needs_changes'].map((s) => {
          const label = s === '' ? 'All' : submissionStatusConfig(s).label
          const isActive = statusFilter === s
          return (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                isActive
                  ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
              }`}
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* List */}
      {submissions.length === 0 ? (
        <div className="empty-state py-20">
          <FileCheck className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300 text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            No project submissions to review.
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Student project submissions will appear here when students submit their work.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-gray-100 dark:border-gray-800 text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium">
            <div className="col-span-3">Student</div>
            <div className="col-span-4">Project</div>
            <div className="col-span-3">Course</div>
            <div className="col-span-2">Status</div>
          </div>
          {/* Rows */}
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {submissions.map((sub) => {
              const sc = submissionStatusConfig(sub.status)
              const StatusIcon = sc.icon
              return (
                <button
                  key={sub.id}
                  onClick={() => navigate(`/trainer/digital-lab/submissions/${sub.id}`)}
                  className="w-full grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-left items-center"
                >
                  <div className="col-span-12 sm:col-span-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {sub.student.name}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {sub.student.student_number}
                    </p>
                  </div>
                  <div className="col-span-12 sm:col-span-4">
                    <p className="text-sm text-gray-700 dark:text-gray-200 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {sub.project.title}
                    </p>
                  </div>
                  <div className="col-span-12 sm:col-span-3 text-sm text-gray-600 dark:text-gray-300 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {sub.course ? sub.course.title : <span className="text-gray-400 dark:text-gray-500">—</span>}
                  </div>
                  <div className="col-span-12 sm:col-span-2 flex items-center gap-1.5">
                    <StatusIcon className={`w-4 h-4 ${sc.color}`} />
                    <span className={`text-xs ${sc.color}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {sc.label}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Submission Detail / Review ───────────────────────────────────────────────

function SubmissionReview() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [reviewStatus, setReviewStatus] = useState('')
  const [feedback, setFeedback] = useState('')

  const { data: submissionData, isLoading, error } = useQuery({
    queryKey: ['trainer-digital-lab-submission', id],
    queryFn: async () => {
      const { data } = await api.get(`/digital-lab/trainer/submissions/${id}`)
      return data
    },
  })

  const submission = submissionData?.submission

  // Fetch submitted files (Slice 8)
  const { data: filesData } = useQuery({
    queryKey: ['trainer-digital-lab-submission-files', id],
    queryFn: async () => {
      const { data } = await api.get(`/digital-lab/trainer/submissions/${id}/files`)
      return data
    },
    enabled: !!submission,
  })

  const handleDownload = async (fileId, filename) => {
    try {
      const { data } = await api.get(`/digital-lab/trainer/submissions/${id}/files/${fileId}/download`)
      // Open the presigned URL in a new tab
      window.open(data.url, '_blank')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to download file')
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }

  // Initialize form when data loads
  useEffect(() => {
    if (submission) {
      setReviewStatus(submission.status === 'submitted' ? 'reviewed' : submission.status)
      setFeedback(submission.feedback || '')
    }
  }, [submission])

  const reviewMutation = useMutation({
    mutationFn: async ({ status, feedback }) => {
      const { data } = await api.put(`/digital-lab/trainer/submissions/${id}/review`, { status, feedback })
      return data
    },
    onSuccess: () => {
      toast.success('Review saved')
      queryClient.invalidateQueries({ queryKey: ['trainer-digital-lab-submission', id] })
      queryClient.invalidateQueries({ queryKey: ['trainer-digital-lab-submissions'] })
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to save review')
    },
  })

  const handleSave = () => {
    if (reviewStatus === 'needs_changes' && !feedback.trim()) {
      toast.error('Feedback is required when requesting changes')
      return
    }
    reviewMutation.mutate({ status: reviewStatus, feedback: feedback.trim() })
  }

  if (isLoading) return <LoadingSpinner />

  if (error || !submission) {
    return (
      <div className="empty-state py-16">
        <AlertTriangle className="w-10 h-10 text-red-400 dark:text-red-500 mx-auto mb-3" />
        <p className="text-gray-600 dark:text-gray-300" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {error?.message || 'Submission not found'}
        </p>
        <Link to="/trainer/digital-lab/submissions" className="inline-flex items-center gap-2 mt-4 text-sm text-orange-600 dark:text-orange-400 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to Submissions
        </Link>
      </div>
    )
  }

  const sc = submissionStatusConfig(submission.status)
  const StatusIcon = sc.icon

  return (
    <div className="space-y-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link to="/trainer/digital-lab/submissions" className="text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Submissions
        </Link>
        <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
        <span className="text-gray-900 dark:text-white font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {submission.project.title}
        </span>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-2xl sm:text-3xl text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}>
            Project Submission
          </h1>
        </div>
      </div>

      {/* Submission info */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Student
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {submission.student.name}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {submission.student.email}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Course
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {submission.course ? submission.course.title : '—'}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Folder className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Project
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {submission.project.title}
            </p>
            {submission.project.description && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {submission.project.description}
              </p>
            )}
            {submission.project.status && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Project status: {submission.project.status === 'not_started' ? 'Not Started' : submission.project.status === 'in_progress' ? 'In Progress' : submission.project.status === 'completed' ? 'Completed' : submission.project.status}
              </p>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Submitted
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {formatDate(submission.submitted_at)}
            </p>
          </div>
        </div>

        {/* Current status */}
        <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mr-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Current Status:
            </span>
            <StatusIcon className={`w-4 h-4 ${sc.color}`} />
            <span className={`text-sm font-medium ${sc.color}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {sc.label}
            </span>
          </div>
        </div>
      </div>

      {/* Submitted files (Slice 8) */}
      {filesData && filesData.files && filesData.files.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-6 space-y-4">
          <div className="flex items-center gap-2">
            <FileIcon className="w-5 h-5 text-orange-500 dark:text-orange-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}>
              Submitted Files
            </h2>
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {filesData.count} file{filesData.count !== 1 ? 's' : ''} · {formatFileSize(filesData.total_size)}
            </span>
          </div>
          <div className="space-y-2">
            {filesData.files.map((file) => (
              <div key={file.id} className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-800 px-4 py-3">
                <FileIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 dark:text-gray-200 truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {file.original_filename}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {formatFileSize(file.size_bytes)}
                  </p>
                </div>
                <button
                  onClick={() => handleDownload(file.id, file.original_filename)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800/50 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing feedback (if already reviewed) */}
      {submission.feedback && submission.status !== 'submitted' && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-6 space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}>
              Previous Feedback
            </h2>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {submission.feedback}
          </p>
          <div className="flex items-center gap-3 pt-2 border-t border-gray-50 dark:border-gray-800/50">
            {submission.reviewed_by_name && (
              <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Reviewed by {submission.reviewed_by_name}
              </span>
            )}
            {submission.reviewed_at && (
              <span className="text-xs text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {formatDate(submission.reviewed_at)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Review form */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-6 space-y-5">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-orange-500 dark:text-orange-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}>
            Review
          </h2>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Status
          </label>
          <div className="flex items-center gap-2">
            {['reviewed', 'needs_changes'].map((s) => {
              const cfg = submissionStatusConfig(s)
              const Icon = cfg.icon
              const isActive = reviewStatus === s
              return (
                <button
                  key={s}
                  onClick={() => setReviewStatus(s)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border transition-colors ${
                    isActive
                      ? `border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 ${cfg.color}`
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  <Icon className="w-4 h-4" />
                  {cfg.label}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Feedback {reviewStatus === 'needs_changes' && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={reviewStatus === 'needs_changes' ? 'Explain what changes the student needs to make...' : 'Optional feedback for the student...'}
            rows={5}
            className="w-full px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:border-orange-400"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          />
          {reviewStatus === 'needs_changes' && !feedback.trim() && (
            <p className="text-xs text-red-500 mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Feedback is required when requesting changes.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={reviewMutation.isPending}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            {reviewMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Save Review
          </button>
          <button
            onClick={() => navigate('/trainer/digital-lab/submissions')}
            className="px-5 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DigitalLabSubmissions() {
  const { id } = useParams()
  return (
    <div className="w-full max-w-4xl mx-auto">
      {id ? <SubmissionReview /> : <SubmissionList />}
    </div>
  )
}
